/** Binary units (MiB / GiB), typical for file sizes */
const BYTES_PER_MB = 1024 ** 2;
const BYTES_PER_GB = 1024 ** 3;

/**
 * Parses a single recording's `eeg_file_size` (bytes) from API payloads.
 * @param {unknown} value
 * @returns {number} non-negative integer bytes, or 0 if missing/invalid
 */
export function parseEegFileSizeBytes(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.floor(n);
}

/**
 * Sums `eeg_file_size` across an array of recordings.
 * @param {Array<{ eeg_file_size?: unknown }>|null|undefined} recordings
 * @returns {number} total bytes
 */
export function sumRecordingFileSizesBytes(recordings) {
  if (!Array.isArray(recordings)) return 0;
  return recordings.reduce(
    (sum, rec) => sum + parseEegFileSizeBytes(rec?.eeg_file_size),
    0,
  );
}

/**
 * Sums `eeg_file_size` for every recording under the given patients.
 * @param {Array<{ recordings?: unknown[] }>|null|undefined} patients
 * @returns {number} total bytes
 */
export function sumPatientsRecordingsFileSizesBytes(patients) {
  if (!Array.isArray(patients)) return 0;
  return patients.reduce(
    (total, patient) => total + sumRecordingFileSizesBytes(patient?.recordings),
    0,
  );
}

/**
 * Human-readable size: MB when under 1 GB, otherwise GB (binary units).
 * @param {number} totalBytes
 * @param {{ mbFractionDigits?: number, gbFractionDigits?: number }} [options]
 * @returns {string} e.g. "12.45 MB" or "1.234 GB"
 */
export function formatTotalBytesAsGb(totalBytes, options = {}) {
  const { mbFractionDigits = 2, gbFractionDigits = 3 } = options;
  if (!Number.isFinite(totalBytes) || totalBytes <= 0) return "0 MB";
  if (totalBytes < BYTES_PER_GB) {
    const mb = totalBytes / BYTES_PER_MB;
    return `${mb.toFixed(mbFractionDigits)} MB`;
  }
  const gb = totalBytes / BYTES_PER_GB;
  return `${gb.toFixed(gbFractionDigits)} GB`;
}
